import { makeId } from "@/utils/";
import DashboardService from "@/store/services/dashboard.service.js";
import CompanySettingService from "@/store/services/companysetting.service.js";
import HeroesService from "@/store/services/heroes.service.js";

export default {
  namespaced: true,
  state: {
    userWidgets: [],
    tempUserWidgets: [],

    // accountants widget config
    defaultConfigAccountants: [
      { type: 'accountants-customers', x: 0, y: 0 },
      { type: 'accountants-profile',   x: 9, y: 0 },
      { type: 'activities-timeline',   x: 9, y: 3 },
    ],
    widgetDefaultConfigsAccountants: [
      { type: 'accountants-customers', name: 'Connected customers',    w: 6, h: 5,  minH: 4,  minW: 6, config: { period: { days: 30 } } },
      { type: 'accountants-profile', name: 'Accountant profile',       w: 3, h: 5,  minH: 4,  minW: 2, config: { period: { days: 30 } } },
      { type: 'activities-timeline', name: 'Account timeline',         w: 3, h: 10, minH: 5,  minW: 3, maxW: 3, config: { object_type: '' }, noResize: false },
    ],

    // customers widget config
    defaultConfig: [
      { type: 'company-profile',      x: 9, y: 11 },
      { type: 'activities-timeline',  x: 9, y: 21 },
      { type: 'cashflow-chart',       x: 0, y: 0 },
      { type: 'payments-overview',    x: 6, y: 10 },
      { type: 'costs',                x: 0, y: 10 },
      { type: 'invoices',             x: 3, y: 10 },
      { type: 'trips',                x: 3, y: 15 },
      { type: 'hours',                x: 0, y: 15 }
    ],
    widgetDefaultConfigs: [
      { type: 'previous-payments-overview', name: 'Previous payments', w: 3, h: 10, minH: 5,  minW: 2, config: { period: { days: 90 } } },
      { type: 'payments-overview', name: 'To be paid',                 w: 3, h: 10, minH: 5,  minW: 2, config: { dir: 'all' } },

      { type: 'rabo-finance', name: 'Rabobank Financing',              w: 3, h: 7,  minH: 7,  minW: 2, x: 9, y: 0, config: { }, noRemove: true, unique: true, },
      { type: 'heroes-program', name: 'Heroes program',                w: 3, h: 4,  minH: 4,  minW: 2, x: 9, y: 7, config: { }, noRemove: true, unique: true, noMove: true, locked: false },
      { type: 'company-profile', name: 'Company profile',              w: 3, h: 5,  minH: 5,  minW: 2, x: 9, y: 11, config: { }, noRemove: true, unique: true, noMove: true, locked: false },
      { type: 'accountant', name: 'Accountant overview',               w: 3, h: 5,  minH: 5,  minW: 2, x: 9, y: 16, config: { }, noResize: true, noRemove: true, unique: true, noMove: true},
      
      { type: 'activities-timeline', name: 'Account timeline',         w: 3, h: 15, minH: 5,  minW: 3, maxW: 3, config: { object_type: '' }, noResize: false, noRemove: true, unique: true, noMove: true},
      { type: 'cashflow-chart', name: 'Revenue and expenses',          w: 9, h: 10, minH: 8,  minW: 3, config: { period: { days: 90 } } },
      { type: 'costs', name: 'Costs overview',                         w: 3, h: 5,  minH: 4,  minW: 2, config: { period: { days: 30 } } },
      { type: 'invoices', name: 'Invoices overview',                   w: 3, h: 5,  minH: 4,  minW: 2, config: { period: { days: 30 } } },
      { type: 'trips', name: 'Trips overview',                         w: 3, h: 5,  minH: 4,  minW: 2, config: { period: { days: 30 } } },
      { type: 'hours', name: 'Hours overview',                         w: 3, h: 5,  minH: 4,  minW: 2, config: { period: { days: 30 } } },
    ],
    widgetGlobalDefaults: {
      x: 0, y: 0,
      premium: false,
      noResize: true,
      noMove: false,
      locked: false,
      unique: false,
      noRemove: false
    },
    financiallyActiveYears: [],
    availableUsers: [],
    dataId: null,
    modalContent: {},
    openModal: false,
    has_accountant: false,
    extraConfig: {},
    heroesStatus: null,
    openFinancingModal: false,
    financingElegibility: false,
    visitingDashboard: false
  },
  getters: {
    getAvailableWidgets: (state, getters) => {
      // Use the accountant-aware widget source
      const source = getters.getWidgetSource;

      // Merge each widget with global defaults (do not overwrite with undefined)
      return source.map(widget => {
        const merged = { ...getters.getGlobalDefaults };
        for (const [k, v] of Object.entries(widget)) {
          if (v !== undefined) merged[k] = v;
        }
        return merged;
      });
    },
    getUserWidgets: state => state.userWidgets,
    getTempUserWidgets: state => state.tempUserWidgets,

    getDefaultConfigSource: (state, getters, rootState, rootGetters) => {
      // If visiting another user's dashboard → always return the non-accountant config
      if (getters.getVisitingDashboard) {
        return state.defaultConfig
      }
      // Otherwise follow the accountant vs non-accountant logic
      return rootGetters['main/getCurrentUser']?.is_accountant
        ? state.defaultConfigAccountants
        : state.defaultConfig
    },

    getDefaultConfig: (state, getters) => getters.getDefaultConfigSource,

    getDefaultConfigByType: (state, getters) => (type) =>
      getters.getDefaultConfigSource.find(item => item.type === type),

    getFullDefaultConfig: (state, getters) => {
      const widgets = [];
      getters.getDefaultConfigSource.forEach(item => {
        widgets.push(Object.assign(getters.getWidgetDefaults(item.type), item));
      });
      return widgets;
    },

    getGlobalDefaults: state => state.widgetGlobalDefaults,
    getWidgetSource: (state, getters, rootState, rootGetters) => {
      // If visiting another user's dashboard → always return the non-accountant config
      if (getters.getVisitingDashboard) {

        return state.widgetDefaultConfigs
      }

      // Otherwise follow the accountant vs non-accountant logic
      return rootGetters['main/getCurrentUser']?.is_accountant
        ? state.widgetDefaultConfigsAccountants
        : state.widgetDefaultConfigs
    },

    getWidgetDefaults: (state, getters) => (type) => {
      // Get the widget definition for the given type from the selected source
      const base = getters.getWidgetSource.find(i => i.type === type) || {};

      // Get the global defaults from another getter
      const globalDefaults = getters.getGlobalDefaults;

      // Start with global defaults and selectively override them with defined widget values
      const merged = { ...globalDefaults };
      for (const [key, value] of Object.entries(base)) {
        // Only overwrite if the widget explicitly defines a value
        if (value !== undefined) merged[key] = value;
      }

      // Return a clean, complete widget configuration
      return merged;
    },

    getWidget: state => id => {
      return state.userWidgets.find(item => item.id === id)
    },
    getFinanciallyActiveYears: state => state.financiallyActiveYears,
    getAvailableUsers: state => state.availableUsers,
    getDataId: state => state.dataId,
    isModalOpen: state => state.openModal,
    getModalContent: state => state.modalContent,
    getHeroesStatus: state => state.heroesStatus,
    getExtraConfig: state => state.extraConfig,
    getHasAccountant: state => state.has_accountant,
    isFinancingModalOpen: state => state.openFinancingModal,
    getFinancingElegibility: state => state.financingElegibility,
    getVisitingDashboard: state => state.visitingDashboard
  },
  mutations: {
    SET_UPDATE_DASHBOARD_CONFIG (state, config) {
      state.userWidgets = config
    },
    SET_EXTRA_CONFIG (state, config) {
      state.extraConfig = config
    },
    ADD_WIDGET (state, widget) {
      widget.id = makeId(16)
      state.userWidgets.push(widget)
    },
    UPDATE_WIDGET_CONFIG (state, payload) {
      const widget = state.userWidgets.find(item => item.id === payload.id)
      widget.config = payload.config
    },
    RESET_TO_DEFAULT (state, widgets) {
      state.userWidgets = []
      state.userWidgets = JSON.parse(JSON.stringify(widgets))

      state.tempUserWidgets = []
      state.tempUserWidgets = JSON.parse(JSON.stringify(widgets))
    },
    DELETE_CONFIG (state) {
      state.userWidgets = []
    },
    SET_WIDGET_IDS (state) {
      state.userWidgets.forEach( widget => {
        widget.id = makeId(16)
      })
    },
    SET_DATA_ID (state, id) {
      state.dataId = id
    },
    SET_USER_WIDGETS (state, widgets) {
      state.userWidgets = widgets
    },
    SET_TEMP_USER_WIDGETS (state, widgets) {
      state.tempUserWidgets = widgets
    },
    SET_FINANCIALLY_ACTIVE_YEARS (state, years) {
      state.financiallyActiveYears = years
    },
    SET_AVAILABLE_USERS (state, users) {
      state.availableUsers = users
    },
    SET_MODAL_CONTENT (state, modalContent) {
      state.modalContent = modalContent
    },
    OPEN_MODAL (state) {
      state.openModal = true
    },
    CLOSE_MODAL (state) {
      state.openModal = false
    },
    SET_HAS_ACCOUNTANT(state, has_accountant) {
      state.has_accountant = has_accountant;
    },
    SET_HEROES_STATUS (state, heroes) {
      state.heroesStatus = heroes
    },
    SET_FINANCING_ELEGIBILITY (state, status) {
      state.financingElegibility = status
    },
    TOGGLE_FINANCING_MODAL (state, open) {
      state.openFinancingModal = open
    },
    SET_VISITING_DASHBOARD (state) {
      state.visitingDashboard = true
    }
  },
  actions: {
    loadDashboardConfig ({ commit, getters, rootGetters, dispatch }, { company, accountants_portal = false }) {
      if (!accountants_portal && rootGetters['main/getCurrentUser']?.is_accountant) {
        commit('SET_VISITING_DASHBOARD')
        commit('SET_USER_WIDGETS', getters.getFullDefaultConfig)
        commit('SET_TEMP_USER_WIDGETS', getters.getFullDefaultConfig)
        commit('SET_WIDGET_IDS')
        return
      }

      return CompanySettingService.retrieve({ company: company.id }).then((response) => {
        commit('SET_DATA_ID', response.data[0].id)

        const full_dashboard_config = (response.data[0].dashboard_config) ? JSON.parse(response.data[0].dashboard_config) : {}

        const current_user = rootGetters['main/getCurrentUser']
        const user_id = current_user.id

        const dashboard_config = (full_dashboard_config[user_id]) ? full_dashboard_config[user_id] : {}

        let parsed_widgets = []

        if (dashboard_config.config) {
          parsed_widgets = (dashboard_config.config.length) ? dashboard_config.config : getters.getFullDefaultConfig
        } else {
          // if not dashboard_config.config and dashboard_config.length means its the old data structure
          parsed_widgets = (dashboard_config.length) ? dashboard_config : getters.getFullDefaultConfig
        }

        const extra_config = (dashboard_config.extra) ? dashboard_config.extra : {}
        commit('SET_EXTRA_CONFIG', extra_config)
        
        let forced_widgets = []

        if (company.has_accountant) {
          commit("SET_HAS_ACCOUNTANT", true);
          forced_widgets.push('accountant')
        }
        
        if (response.data[0].rabo_on_dashboard) {
          commit('SET_FINANCING_ELEGIBILITY', true)
        }

        return dispatch('loadHeroesStatus').then(() => {
          if (!extra_config.closed_pbl && getters.getFinancingElegibility) {
            forced_widgets.push('rabo-finance')
          }
          if (!extra_config.closed_heroes && getters.getHeroesStatus) {
            forced_widgets.push('heroes-program')
          } else {
            // if heroes is present, reset dashboard config
            if (parsed_widgets.find(item => item.type === 'heroes-program')) {
              dispatch('resetToDefault')
              return false;
            }
          }

          if (forced_widgets.length > 0) {
            dispatch('forceLoadWidgets', { parsed_widgets: parsed_widgets, widget_names: forced_widgets }).then((response) => {
              commit('SET_USER_WIDGETS', response)
              commit('SET_TEMP_USER_WIDGETS', response)
              commit('SET_WIDGET_IDS')
            })
          } else {
            commit('SET_USER_WIDGETS', parsed_widgets)
            commit('SET_TEMP_USER_WIDGETS', parsed_widgets)
            commit('SET_WIDGET_IDS')
          }

        })

      })
    },
    resetToDefault ({ commit, getters }) {

      if (getters.getVisitingDashboard) {
        commit('SET_USER_WIDGETS', getters.getFullDefaultConfig)
        commit('SET_TEMP_USER_WIDGETS', getters.getFullDefaultConfig)
        commit('SET_WIDGET_IDS')
        return
      }

      commit('SET_EXTRA_CONFIG', {})

      let parsed_widgets = getters.getFullDefaultConfig
      if (getters.getHasAccountant) {
        let accountant_widgets = parsed_widgets.filter(widget => widget.type === 'accountant')
        if (!accountant_widgets.length) {
          parsed_widgets.push(Object.assign(getters.getWidgetDefaults('accountant'), { type: 'accountant', x: 9, y: 16 }))
        }
      }

      if (getters.getFinancingElegibility) {
        let rabo_widgets = parsed_widgets.filter(widget => widget.type === 'rabo-finance')
        if (!rabo_widgets.length) {
          parsed_widgets.push(Object.assign(getters.getWidgetDefaults('rabo-finance'), { type: 'rabo-finance', x: 9, y: 0 }))
        }
      }

      if (!getters.getExtraConfig.closed_heroes && getters.getHeroesStatus) {
        let hero_widgets = parsed_widgets.filter(widget => widget.type === 'heroes-program')
        if (!hero_widgets.length) {
          parsed_widgets.push(Object.assign(getters.getWidgetDefaults('heroes-program'), { type: 'heroes-program', x: 9, y: 7 }))
        }
      }

      commit('RESET_TO_DEFAULT', parsed_widgets);
      commit('SET_WIDGET_IDS');
    },
    loadHeroesStatus ({ commit }) {
      return HeroesService.list().then((response) => {
        commit('SET_HEROES_STATUS', (response.data.length > 0) ? response.data[0] : null)
      })
    },
    loadAvailableUsers ({ commit, rootGetters }) {
      const company_id = rootGetters['main/getCompanyId']
      DashboardService.availableUsers({
        extended: "True",
        company_profile: company_id
      }).then((response) => {
        const availableUsers = []
        response.data.forEach(item => {
          availableUsers.push({ name: item.user.display_name, event: { name: 'set-config', config: { user: item.user } } })
        });
        commit('SET_AVAILABLE_USERS', availableUsers)
      })
    },
    loadFinanciallyActiveYears ({ commit, rootGetters }) {
      const company_id = rootGetters['main/getCompanyId']
      DashboardService.financiallyActiveYears(company_id).then((response) => {
        const activeYears = []
        response.data.forEach(year => {
          activeYears.push({ name: year, event: { name: 'set-config', config: { year: year } } })
        });
        commit('SET_FINANCIALLY_ACTIVE_YEARS', activeYears)
      })
    },
    forceLoadWidgets ({ getters }, payload) {
      payload.widget_names.forEach( widget_name => {
        let unique_widget = payload.parsed_widgets.filter(widget => widget.type === widget_name);
        if (!unique_widget.length) {
          payload.parsed_widgets.push(Object.assign(getters.getWidgetDefaults(widget_name), getters.getDefaultConfigByType(widget_name)))
        }
      })
      return payload.parsed_widgets
    },
    saveDashboardConfig ({ getters, rootGetters }, config) {
      if (getters.getVisitingDashboard) {
        return
      }

      let full_config = { config: config }

      const extra_config = getters.getExtraConfig
      if (extra_config) {
        full_config = { ...full_config, extra: { ...extra_config } }
      }
      
      CompanySettingService.retrieve({ company: rootGetters['main/getCurrentCompany'].id }).then((response) => {
        const full_dashboard_config = (response.data[0].dashboard_config) ? JSON.parse(response.data[0].dashboard_config) : {}

        // removing null values for old dashboard_config
        // due to incorrect variable type set (array instead of object)
        const clean_full_dashboard_config = {}
        for (let index = 0; index < full_dashboard_config.length; index++) {
          const element = full_dashboard_config[index];
          if (element !== null) {
            clean_full_dashboard_config[index] = element
          }
        }

        const company_id = rootGetters['main/getCompanyId']
        const user_id = rootGetters['main/getCurrentUser'].id
        clean_full_dashboard_config[user_id] = full_config

        const data = {
          dashboard_config: JSON.stringify(clean_full_dashboard_config),
          id: getters.getDataId,
          company: company_id
        }

        CompanySettingService.update(data)
      })
      
    },
    openModal ({ commit }, modalContent) {
      commit('SET_MODAL_CONTENT', modalContent);
      commit('OPEN_MODAL');
    },
    closeModal ({ commit }) {
      commit('CLOSE_MODAL');
      commit('SET_MODAL_CONTENT', {});
    },
    updateExtraConfig ({ commit }, config) {
      commit('SET_EXTRA_CONFIG', config);
    },
    updateTempUserWidgets ({ commit }, config) {
      commit('SET_TEMP_USER_WIDGETS', config);
    }
  }
}
